import { unMasked } from "./masked";

const CNPJ_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const ASCII_BASE = "0".charCodeAt(0);

const calcCnpjCheckDigit = (sum: number) => {
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

const calcCnpjCheckDigits = (base12: string) => {
  let sumDv1 = 0;
  let sumDv2 = 0;

  for (let i = 0; i < 12; i++) {
    const value = base12.charCodeAt(i) - ASCII_BASE;
    sumDv1 += value * CNPJ_WEIGHTS[i + 1];
    sumDv2 += value * CNPJ_WEIGHTS[i];
  }

  const dv1 = calcCnpjCheckDigit(sumDv1);
  sumDv2 += dv1 * CNPJ_WEIGHTS[12];
  const dv2 = calcCnpjCheckDigit(sumDv2);

  return `${dv1}${dv2}`;
};

const isRepeatedDigits = (value: string) => /^(\d)\1+$/.test(value);

export const isValidCpf = (value: string): boolean => {
  const cpf = unMasked(value);

  if (!/^\d{11}$/.test(cpf) || isRepeatedDigits(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(cpf[i]) * (10 - i);
  let digit = (sum * 10) % 11;
  if (digit === 10) digit = 0;
  if (digit !== Number(cpf[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(cpf[i]) * (11 - i);
  digit = (sum * 10) % 11;
  if (digit === 10) digit = 0;

  return digit === Number(cpf[10]);
};

export const isValidCnpj = (value: string): boolean => {
  const cnpj = unMasked(value);

  // 12 alfanuméricos (A-Z/0-9) + 2 dígitos verificadores numéricos
  if (!/^([A-Z\d]){12}\d{2}$/.test(cnpj)) return false;
  if (cnpj === "00000000000000") return false;

  const expectedDv = calcCnpjCheckDigits(cnpj.slice(0, 12));
  return cnpj.slice(12) === expectedDv;
};

/**
 * Valida CPF (somente numérico) ou CNPJ (numérico legado / alfanumérico).
 * Retorna `true` se válido, ou mensagem de erro.
 */
export const validateCpfCnpj = (value?: string): true | string => {
  const cleaned = unMasked(value || "");

  if (!cleaned) return "CPF/CNPJ obrigatório";

  if (cleaned.length <= 11) {
    if (!/^\d+$/.test(cleaned)) {
      return "CPF deve conter apenas números";
    }
    if (cleaned.length !== 11 || !isValidCpf(cleaned)) {
      return "CPF inválido";
    }
    return true;
  }

  if (cleaned.length !== 14) {
    return "CNPJ deve ter 14 caracteres";
  }

  if (!/^\d{2}$/.test(cleaned.slice(12))) {
    return "Dígitos verificadores do CNPJ devem ser numéricos";
  }

  if (!isValidCnpj(cleaned)) {
    return "CNPJ inválido. Verifique o número e os dígitos verificadores";
  }

  return true;
};
